/**
 * Layout constants for consistent spacing, sizing, and layout patterns
 * across all org slug pages
 */

/**
 * Standard spacing values
 */
export const SPACING = {
  /** Button group gap */
  BUTTON_GROUP: "gap-3",
  /** Button group gap (larger) */
  BUTTON_GROUP_LARGE: "gap-4",
  /** Card padding (large) */
  CARD_PADDING_LG: "p-8",
  /** Card padding (medium) */
  CARD_PADDING_MD: "p-6",
  /** Card padding (small) */
  CARD_PADDING_SM: "p-4 sm:p-6",
  /** Main content padding - horizontal */
  CONTENT_PADDING_X: "px-4 sm:px-6 lg:px-8",
  /** Main content padding - vertical */
  CONTENT_PADDING_Y: "py-8 lg:py-12",
  /** Form field spacing */
  FORM_FIELD: "space-y-4",
  /** Form section spacing */
  FORM_SECTION: "space-y-8",
  /** Grid gap for card grids */
  GRID_GAP: "gap-6",
  /** Major section spacing (vertical) */
  SECTION_MAJOR: "py-12 lg:py-16",
  /** Minor section spacing (vertical) */
  SECTION_MINOR: "py-8 lg:py-12",
  /** Within sections spacing */
  WITHIN_SECTION: "space-y-6",
  /** Within sections spacing (larger) */
  WITHIN_SECTION_LARGE: "space-y-8",
} as const;

/**
 * Typography scale constants
 */
export const TYPOGRAPHY = {
  /** Body text */
  BODY: "text-base",

  /** Description text */
  DESCRIPTION: "text-base sm:text-lg text-default-600",

  /** Form section header (h3) */
  FORM_SECTION_HEADER: "text-xl sm:text-2xl font-semibold",

  /** Page title (h1) */
  PAGE_TITLE: "text-3xl sm:text-4xl lg:text-5xl font-bold",

  /** Section heading (h2) */
  SECTION_HEADING: "text-2xl sm:text-3xl font-semibold",

  /** Small text */
  SMALL: "text-sm",

  /** Subsection heading (h3) */
  SUBSECTION_HEADING: "text-xl sm:text-2xl font-semibold",
  /** Extra small text */
  XS: "text-xs",
} as const;

/**
 * Grid layout patterns
 */
export const GRID_LAYOUTS = {
  /** Card grid - responsive 1/2/3 columns */
  CARD_GRID: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6",

  /** Form fields grid - responsive 1/2 columns */
  FORM_FIELDS: "grid grid-cols-1 md:grid-cols-2 gap-4",

  /** Single column grid */
  SINGLE_COLUMN: "grid grid-cols-1 gap-6",

  /** Stats grid - responsive 2/4 columns */
  STATS_GRID: "grid grid-cols-2 md:grid-cols-4 gap-6",
} as const;

/**
 * Component sizing constants
 */
export const COMPONENT_SIZES = {
  /** Button height (standard) */
  BUTTON_HEIGHT: "h-10",
  /** Button height (large) */
  BUTTON_HEIGHT_LARGE: "h-12",

  /** Icon size (large) */
  ICON_LG: "h-8 w-8",

  /** Icon size (medium) */
  ICON_MD: "h-6 w-6",

  /** Icon size (small) */
  ICON_SM: "h-5 w-5",

  /** Input height (standard) */
  INPUT_HEIGHT: "h-10",

  /** Input height (large) */
  INPUT_HEIGHT_LARGE: "h-12",
} as const;

/**
 * Page header spacing
 */
export const PAGE_HEADER = {
  /** Spacing below page header */
  MARGIN_BOTTOM: "mb-8 lg:mb-12",
} as const;
